import useHidePostActions from "@/auth/hooks/useHidePostActions";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "@/components/ui/use-toast";
import { useFetchDataV2 } from "@/hooks/useFetchDataV2";
import usePostData from "@/hooks/usePostData";
import { useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  CirclePlus,
  EllipsisVertical,
  GripVertical,
  Loader2,
  Settings2,
  SquarePen,
  Trash,
  X,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import DefaultFormJson from "../../DefaultForm.json";
import CreateForm from "./CreateForm";

const DefaultForm: Step[] = DefaultFormJson as Step[];
const API_URL = import.meta.env.VITE_API_ENDPOINT;
const INTRO_STEP_API_NAME = "introduction";
const INTRO_STEP_LABEL = "introduction";
const PROTECTED_STEP_API_NAME = "descriptif_du_projet";
const PROTECTED_STEP_LABEL = "descriptif du projet";
const LOCKED_FINANCING_STEP_API_NAME = "financement";
const LOCKED_FINANCING_STEP_LABEL = "financement";
const PROTECTED_DESCRIPTIF_FIELDS = new Set([
  "name",
  "description",
  "type",
  "launchdate",
  "expectedduration",
  "country",
  "searchfinancinginternal",
]);
const PROTECTED_DESCRIPTIF_ORDER = [
  "name",
  "description",
  "launchdate",
  "expectedduration",
  "country",
  "type",
  "searchfinancinginternal",
];
const HIDDEN_CALL_DESCRIPTIF_FIELDS = [
  "name",
  "description",
  "type",
  "projecttype",
  "launchdate",
  "expectedduration",
  "country",
  "searchfinancinginternal",
];

export type FieldType =
  | "text"
  | "textarea"
  | "number"
  | "date"
  | "select"
  | "upload"
  | "funding"
  | "radio"
  | "checkbox"
  | "Map"
  | "plantedSpecies"
  | "protectedSpecies"
  | "country"
  | "projectType";

export type FieldScoringMode = "none" | "auto_choice" | "manual";

export interface FieldScoringConfig {
  enabled: boolean;
  mode: Exclude<FieldScoringMode, "none">;
  pointsByChoice?: Record<string, number>;
  maxPoints?: number | null;
}

export interface FieldDecisionRulesConfig {
  disqualifyingChoices?: string[];
}

export interface Field {
  id?: number;
  name: string;
  label: string;
  type: FieldType;
  required: boolean;
  options?: string[] | null;
  scoring?: FieldScoringConfig | null;
  decisionRules?: FieldDecisionRulesConfig | null;
  visibilityMode?: "all" | "show_only" | "hide_only";
  visibilityProjectTypeIds?: number[];
  nativeField?: string;
  step: number;
  parentId?: number;
  sectionId?: number | null;
}

export interface Section {
  id?: number;
  name: string;
  label: string;
  description?: string | null;
  step: number;
  fields: Field[];
}

export interface Step {
  id?: number;
  step: number;
  nameStep: string;
  description?: string | null;
  options?: Record<string, unknown> | null;
  visibilityMode?: "all" | "show_only" | "hide_only";
  visibilityProjectTypeIds?: number[];
  apiName?: string;
  type?: string;
  fields: Field[];
  sections?: Section[];
}

type CallFormScoringDecisionMode =
  | "auto_threshold"
  | "auto_total"
  | "manual_total";

type CallFormScoringSettings = {
  enabled: boolean;
  decisionMode?: CallFormScoringDecisionMode;
  acceptancePercentage?: number | null;
  rejectionPercentage?: number | null;
};

const CALL_FORM_SCORING_SETTINGS_KEY = "callFormScoringSettings";
const FORMS_LIBRARY_CACHE_ID = "parameters-forms-library";
const FORMS_CACHE_TTL_MS = 1000 * 60 * 60;
const PROJECT_TYPES_CACHE_ID = "project-types-parameters";
const PROJECT_TYPES_CACHE_TTL_MS = 1000 * 60 * 60;
const FIELD_FORM_STEPS_CACHE_TTL_MS = 1000 * 60 * 30;

type ApiField = {
  id?: number;
  name: string;
  label: string;
  type: string;
  required?: boolean;
  options?: unknown;
  nativeField?: string;
  step?: number;
  parentId?: number;
  sectionId?: number | null;
};

type ApiSection = {
  id?: number;
  name: string;
  label: string;
  description?: string | null;
  step?: number;
  fields?: ApiField[];
};

type ApiStep = {
  id?: number;
  step: number;
  name: string;
  label: string;
  type?: string;
  options?: unknown;
  description?: string | null;
  fields: ApiField[];
  sections?: ApiSection[];
};

type UpsertFieldPayload = {
  name: string;
  label: string;
  type: FieldType;
  required: boolean;
  options?: unknown;
  nativeField?: string;
  step: number;
  parentId?: number;
};

const VALID_FIELD_TYPES: FieldType[] = [
  "text",
  "textarea",
  "number",
  "date",
  "select",
  "upload",
  "funding",
  "radio",
  "checkbox",
  "Map",
  "plantedSpecies",
  "protectedSpecies",
  "country",
  "projectType",
];

function toSafeFieldType(type: string): FieldType {
  return VALID_FIELD_TYPES.includes(type as FieldType)
    ? (type as FieldType)
    : "text";
}

function toApiStepName(label: string, fallbackStep: number): string {
  const trimmed = label.trim();
  if (!trimmed) {
    return `step_${fallbackStep}`;
  }
  const cleaned = trimmed
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

  return cleaned || `step_${fallbackStep}`;
}

const extractChoiceOptions = (rawOptions: unknown): string[] | null => {
  if (Array.isArray(rawOptions)) {
    return rawOptions.map((option) => String(option));
  }
  if (
    rawOptions &&
    typeof rawOptions === "object" &&
    Array.isArray((rawOptions as any).choices)
  ) {
    return (rawOptions as any).choices.map((option: any) => String(option));
  }
  return null;
};

const asArray = <T,>(value: any): T[] => {
  if (Array.isArray(value)) return value as T[];
  if (value && Array.isArray(value["hydra:member"])) {
    return value["hydra:member"] as T[];
  }
  return [];
};

const extractVisibilityConfig = (rawOptions: unknown): {
  mode: "all" | "show_only" | "hide_only";
  projectTypeIds: number[];
} => {
  let source: any = rawOptions;
  if (typeof source === "string") {
    try {
      source = JSON.parse(source);
    } catch {
      source = null;
    }
  }

  if (!source || typeof source !== "object" || Array.isArray(source)) {
    return { mode: "all", projectTypeIds: [] };
  }

  const modeRaw = String((source as any).visibilityMode ?? "all").trim().toLowerCase();
  const mode = modeRaw === "show_only" || modeRaw === "hide_only" ? modeRaw : "all";
  const idsRaw = (source as any).visibilityProjectTypeIds;
  const projectTypeIds = Array.isArray(idsRaw)
    ? idsRaw
        .map((id: any) => Number(id))
        .filter((id: number) => Number.isFinite(id) && id > 0)
    : [];

  return { mode, projectTypeIds };
};

const extractScoringConfig = (
  rawOptions: unknown,
): FieldScoringConfig | null => {
  let source: any = rawOptions;
  if (typeof source === "string") {
    try {
      source = JSON.parse(source);
    } catch {
      source = null;
    }
  }

  if (!source || typeof source !== "object" || Array.isArray(source)) {
    return null;
  }

  const scoring = (source as any).scoring;
  if (!scoring || typeof scoring !== "object" || Array.isArray(scoring)) {
    return null;
  }

  const enabled = Boolean((scoring as any).enabled);
  const modeRaw = String((scoring as any).mode ?? "")
    .trim()
    .toLowerCase();
  const mode =
    modeRaw === "auto_choice" || modeRaw === "manual" ? modeRaw : null;
  if (!enabled || !mode) {
    return null;
  }

  const pointsByChoice =
    scoring.pointsByChoice &&
    typeof scoring.pointsByChoice === "object" &&
    !Array.isArray(scoring.pointsByChoice)
      ? Object.fromEntries(
          Object.entries(scoring.pointsByChoice as Record<string, unknown>).map(
            ([key, value]) => [key, Number(value) || 0],
          ),
        )
      : undefined;

  const maxPoints =
    scoring.maxPoints !== undefined && scoring.maxPoints !== null
      ? Number(scoring.maxPoints)
      : null;

  return {
    enabled: true,
    mode,
    pointsByChoice,
    maxPoints:
      typeof maxPoints === "number" && Number.isFinite(maxPoints)
        ? maxPoints
        : null,
  };
};

const extractDecisionRulesConfig = (
  rawOptions: unknown,
): FieldDecisionRulesConfig | null => {
  let source: any = rawOptions;
  if (typeof source === "string") {
    try {
      source = JSON.parse(source);
    } catch {
      source = null;
    }
  }

  if (!source || typeof source !== "object" || Array.isArray(source)) {
    return null;
  }

  const decisionRules = (source as any).decisionRules;
  if (
    !decisionRules ||
    typeof decisionRules !== "object" ||
    Array.isArray(decisionRules)
  ) {
    return null;
  }

  const disqualifyingChoices = Array.isArray(
    (decisionRules as any).disqualifyingChoices,
  )
    ? ((decisionRules as any).disqualifyingChoices as unknown[])
        .map((choice) => String(choice).trim())
        .filter(Boolean)
    : [];

  if (disqualifyingChoices.length === 0) {
    return null;
  }

  return {
    disqualifyingChoices,
  };
};

const buildOptionsWithVisibility = (
  rawOptions: unknown,
  mode: "all" | "show_only" | "hide_only" = "all",
  projectTypeIds: number[] = [],
): Record<string, unknown> => {
  const base =
    rawOptions && typeof rawOptions === "object" && !Array.isArray(rawOptions)
      ? { ...(rawOptions as Record<string, unknown>) }
      : {};

  const choices = Array.isArray(rawOptions)
    ? rawOptions.map((option) => String(option))
    : Array.isArray((base as any).choices)
      ? ((base as any).choices as any[]).map((option) => String(option))
      : [];

  delete (base as any).visibilityMode;
  delete (base as any).visibilityProjectTypeIds;

  return {
    ...base,
    choices,
    visibilityMode: mode,
    visibilityProjectTypeIds: projectTypeIds,
  };
};

function mapApiToSteps(apiData: ApiStep[]): Step[] {
  const getProtectedFieldOrder = (field: ApiField) => {
    const keyNative = (field.nativeField ?? "").trim().toLowerCase();
    const index = PROTECTED_DESCRIPTIF_ORDER.indexOf(keyNative);
    return index >= 0 ? index : Number.MAX_SAFE_INTEGER;
  };

  return [...apiData]
    .sort((a, b) => a.step - b.step)
    .map((parent) => ({
      id: parent.id,
      step: Number(parent.step),
      nameStep: parent.label || parent.name || `Étape ${parent.step}`,
      options:
        parent.options &&
        typeof parent.options === "object" &&
        !Array.isArray(parent.options)
          ? { ...(parent.options as Record<string, unknown>) }
          : null,
      visibilityMode: extractVisibilityConfig(parent.options).mode,
      visibilityProjectTypeIds:
        extractVisibilityConfig(parent.options).projectTypeIds,
      description:
        `${parent.description ?? ""}`.trim() !== ""
          ? String(parent.description ?? "")
          : (parent.name ?? "").trim().toLowerCase() === INTRO_STEP_API_NAME &&
              parent.options &&
              typeof parent.options === "object" &&
              !Array.isArray(parent.options)
            ? String((parent.options as Record<string, unknown>).description ?? "")
            : (parent.description ?? null),
      apiName: parent.name,
      type: parent.type ?? "step",
      fields: [...(parent.fields ?? [])]
        .sort((a, b) => {
          const isDescriptifStep =
            (parent.name ?? "").trim().toLowerCase() ===
              PROTECTED_STEP_API_NAME ||
            (parent.label ?? "").trim().toLowerCase() === PROTECTED_STEP_LABEL;

          if (!isDescriptifStep) {
            return (a.step ?? 0) - (b.step ?? 0);
          }

          const aOrder = getProtectedFieldOrder(a);
          const bOrder = getProtectedFieldOrder(b);
          if (aOrder !== bOrder) {
            return aOrder - bOrder;
          }

          return (a.step ?? 0) - (b.step ?? 0);
        })
        .map((child) => ({
          id: child.id,
          name: child.name,
          label: child.label,
          type: toSafeFieldType(child.type),
          required: Boolean(child.required),
          options: extractChoiceOptions(child.options),
          scoring: extractScoringConfig(child.options),
          decisionRules: extractDecisionRulesConfig(child.options),
          visibilityMode: extractVisibilityConfig(child.options).mode,
          visibilityProjectTypeIds:
            extractVisibilityConfig(child.options).projectTypeIds,
          nativeField: child.nativeField,
          step: Number(child.step ?? 0),
          parentId: child.parentId,
          sectionId: child.sectionId ?? null,
        })),
      sections: [...(parent.sections ?? [])]
        .sort((a, b) => (a.step ?? 0) - (b.step ?? 0))
        .map((section) => ({
          id: section.id,
          name: section.name,
          label: section.label,
          description: section.description ?? null,
          step: Number(section.step ?? 0),
          fields: [...(section.fields ?? [])]
            .sort((a, b) => (a.step ?? 0) - (b.step ?? 0))
            .map((field) => ({
              id: field.id,
              name: field.name,
              label: field.label,
              type: toSafeFieldType(field.type),
                required: Boolean(field.required),
                options: extractChoiceOptions(field.options),
                scoring: extractScoringConfig(field.options),
                decisionRules: extractDecisionRulesConfig(field.options),
                visibilityMode: extractVisibilityConfig(field.options).mode,
                visibilityProjectTypeIds:
                  extractVisibilityConfig(field.options).projectTypeIds,
              nativeField: field.nativeField,
              step: Number(field.step ?? 0),
              parentId: field.parentId,
              sectionId: field.sectionId ?? section.id ?? null,
            })),
        })),
    }));
}

function normalizeStepsForUi(steps: Step[]): Step[] {
  return [...steps]
    .sort((a, b) => {
      const aIsIntro =
        (a.apiName ?? "").trim().toLowerCase() === INTRO_STEP_API_NAME ||
        (a.nameStep ?? "").trim().toLowerCase() === INTRO_STEP_LABEL ||
        (a.type ?? "").trim().toLowerCase() === "intro";
      const bIsIntro =
        (b.apiName ?? "").trim().toLowerCase() === INTRO_STEP_API_NAME ||
        (b.nameStep ?? "").trim().toLowerCase() === INTRO_STEP_LABEL ||
        (b.type ?? "").trim().toLowerCase() === "intro";
      if (aIsIntro && !bIsIntro) return -1;
      if (!aIsIntro && bIsIntro) return 1;
      return a.step - b.step;
    })
    .map((step, index) => ({
      ...step,
      step: index + 1,
    }));
}

const getCallFormScoringSettings = (
  steps: Step[],
): CallFormScoringSettings => {
  const introStep = steps.find(
    (step) =>
      (step.apiName ?? "").trim().toLowerCase() === INTRO_STEP_API_NAME ||
      (step.nameStep ?? "").trim().toLowerCase() === INTRO_STEP_LABEL ||
      (step.type ?? "").trim().toLowerCase() === "intro",
  );
  const raw =
    introStep?.options &&
    typeof introStep.options === "object" &&
    !Array.isArray(introStep.options)
      ? (introStep.options as Record<string, unknown>)[
          CALL_FORM_SCORING_SETTINGS_KEY
        ]
      : null;

  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return { enabled: false };
  }

  const source = raw as Record<string, unknown>;
  const decisionModeRaw = String(source.decisionMode ?? "");
  const acceptancePercentageRaw = source.acceptancePercentage;
  const rejectionPercentageRaw = source.rejectionPercentage;

  return {
    enabled: Boolean(source.enabled),
    decisionMode:
      decisionModeRaw === "auto_threshold" ||
      decisionModeRaw === "auto_auto_only" ||
      decisionModeRaw === "auto_total" ||
      decisionModeRaw === "manual_total"
        ? ((decisionModeRaw === "auto_auto_only"
            ? "auto_threshold"
            : decisionModeRaw) as CallFormScoringDecisionMode)
        : undefined,
    acceptancePercentage:
      acceptancePercentageRaw !== undefined && acceptancePercentageRaw !== null
        ? Number(acceptancePercentageRaw)
        : null,
    rejectionPercentage:
      rejectionPercentageRaw !== undefined && rejectionPercentageRaw !== null
        ? Number(rejectionPercentageRaw)
        : null,
  };
};

function buildInitializePayload() {
  return {
    steps: DefaultForm.map((item, index) => ({
      step: index + 1,
      name:
        toApiStepName(item.nameStep, index + 1) === INTRO_STEP_API_NAME
          ? INTRO_STEP_API_NAME
          : toApiStepName(item.nameStep, index + 1),
      label: item.nameStep,
      description: item.description ?? null,
      type:
        toApiStepName(item.nameStep, index + 1) === INTRO_STEP_API_NAME
          ? "intro"
          : "step",
      options:
        toApiStepName(item.nameStep, index + 1) === INTRO_STEP_API_NAME
          ? { description: item.description ?? "" }
          : undefined,
      fields: item.fields.map((field, fieldIndex) => ({
        name: field.name,
        label: field.label,
        type: field.type,
        required: field.required,
        options: field.options ?? null,
        nativeField: field.nativeField,
        step: fieldIndex + 1,
      })),
      sections: (item.sections ?? []).map((section, sectionIndex) => ({
        nameSection:
          (section as any).name ??
          (section as any).nameSection ??
          toApiStepName(
            section.label ?? `section_${sectionIndex + 1}`,
            sectionIndex + 1,
          ),
        label: section.label,
        description: section.description ?? null,
        step: section.step ?? sectionIndex + 1,
        fields: (section.fields ?? []).map((field, fieldIndex) => ({
          name: field.name,
          label: field.label,
          type: field.type,
          required: field.required,
          options: field.options ?? null,
          nativeField: field.nativeField,
          step: field.step ?? fieldIndex + 1,
        })),
      })),
    })),
  };
}

function ParametersForms() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const callIdFromUrl = useMemo(() => {
    const raw = searchParams.get("callId");
    const parsed = raw ? Number(raw) : NaN;
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }, [searchParams]);
  const preservedCallIdFromUrl = useMemo(() => {
    const raw = searchParams.get("preservedCallId");
    const parsed = raw ? Number(raw) : NaN;
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }, [searchParams]);
  const projectTypeIdFromUrl = useMemo(() => {
    const raw = searchParams.get("projectTypeId");
    const parsed = raw ? Number(raw) : NaN;
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }, [searchParams]);
  const callProgramName = useMemo(() => {
    const raw = searchParams.get("programName");
    return raw && raw.trim().length > 0 ? raw.trim() : null;
  }, [searchParams]);
  const showGlobalCallWarning = useMemo(
    () =>
      callIdFromUrl === null &&
      preservedCallIdFromUrl === null &&
      searchParams.get("globalCallWarning") === "1",
    [callIdFromUrl, preservedCallIdFromUrl, searchParams],
  );
  const isCallSpecificEditor = callIdFromUrl !== null || preservedCallIdFromUrl !== null;
  const isProjectTypeEditor = projectTypeIdFromUrl !== null;
  const [editableCallProgramName, setEditableCallProgramName] = useState("");
  const [isEditingCallProgramName, setIsEditingCallProgramName] = useState(false);
  const [savingCallProgramName, setSavingCallProgramName] = useState(false);
  const appendScope = (url: string) => {
    const separator = url.includes("?") ? "&" : "?";
    if (callIdFromUrl) {
      return `${url}${separator}callId=${callIdFromUrl}`;
    }
    if (preservedCallIdFromUrl) {
      return `${url}${separator}preservedCallId=${preservedCallIdFromUrl}`;
    }
    if (projectTypeIdFromUrl) {
      return `${url}${separator}projectTypeId=${projectTypeIdFromUrl}`;
    }
    return url;
  };
  const fieldFormsCacheScope = useMemo(() => {
    if (callIdFromUrl) return `call:${callIdFromUrl}`;
    if (preservedCallIdFromUrl) return `preserved-call:${preservedCallIdFromUrl}`;
    if (projectTypeIdFromUrl) return `project-type:${projectTypeIdFromUrl}`;
    return "global";
  }, [callIdFromUrl, preservedCallIdFromUrl, projectTypeIdFromUrl]);
  const fieldFormsGlobalQueryKey = useMemo(
    () => ["parameters-field-forms-global", fieldFormsCacheScope],
    [fieldFormsCacheScope],
  );
  const hidePostActions = useHidePostActions();
  const canEditForm = !hidePostActions;
  const [steps, setSteps] = useState<Step[]>([]);
  const [editStep, setEditStep] = useState<number>(-1);
  const [selectedStep, setSelectedStep] = useState(1);
  const [stepLabelDraft, setStepLabelDraft] = useState("");
  const [loadingSteps, setLoadingSteps] = useState(true);
  const [inlineError, setInlineError] = useState<string | null>(null);
  const [draggedStepId, setDraggedStepId] = useState<number | null>(null);
  const [dragOverStepId, setDragOverStepId] = useState<number | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [blockedDeleteDialogOpen, setBlockedDeleteDialogOpen] = useState(false);
  const [deletingForm, setDeletingForm] = useState(false);
  const [checkingDeleteUsage, setCheckingDeleteUsage] = useState(false);
  const [showModelImpactWarning, setShowModelImpactWarning] = useState(true);
  const [scoringSettingsOpen, setScoringSettingsOpen] = useState(false);
  const [scoringSettingsDraft, setScoringSettingsDraft] =
    useState<CallFormScoringSettings>({ enabled: false });
  const pendingOptimisticStepLabelsRef = useRef<Record<number, string>>({});
  const pendingOptimisticSectionsRef = useRef<Record<number, {
    parentId: number;
    section: Section;
  }>>({});
  const pendingOptimisticFieldsRef = useRef<Record<number, {
    parentId: number;
    rootStepId: number;
    field: Field;
    payload: UpsertFieldPayload;
  }>>({});

  const { fetchData } = useFetchDataV2<unknown>("");
  const formsLibrary = useFetchDataV2<any>(`${API_URL}/field-forms/library`, {
    cacheId: FORMS_LIBRARY_CACHE_ID,
    staleTime: FORMS_CACHE_TTL_MS,
    gcTime: FORMS_CACHE_TTL_MS,
    browserCache: true,
    browserCacheKey: FORMS_LIBRARY_CACHE_ID,
    browserCacheTtlMs: FORMS_CACHE_TTL_MS,
  });
  const projectTypes = useFetchDataV2<any[]>(`${API_URL}/project-types`, {
    cacheId: PROJECT_TYPES_CACHE_ID,
    staleTime: PROJECT_TYPES_CACHE_TTL_MS,
    gcTime: PROJECT_TYPES_CACHE_TTL_MS,
    browserCache: true,
    browserCacheKey: PROJECT_TYPES_CACHE_ID,
    browserCacheTtlMs: PROJECT_TYPES_CACHE_TTL_MS,
  });
  const projectTypeOptions = useMemo(
    () =>
      asArray<any>(projectTypes.data)
        .map((type: any) => ({
          id: Number(type?.id),
          label: String(type?.name ?? `Type ${type?.id ?? ""}`).trim(),
        }))
        .filter(
          (item) => Number.isFinite(item.id) && item.id > 0 && item.label !== "",
        ),
    [projectTypes.data],
  );
  const projectTypeName = useMemo(() => {
    if (!projectTypeIdFromUrl) return null;
    return (
      projectTypeOptions.find((item) => item.id === projectTypeIdFromUrl)?.label ??
      null
    );
  }, [projectTypeIdFromUrl, projectTypeOptions]);
  const { postData, loading: loadingMutation } = usePostData<unknown>();
  const didTryInitializeRef = useRef(false);
  const currentLibraryItem = useMemo(() => {
    const typeForms = Array.isArray(formsLibrary.data?.projectTypeForms)
      ? formsLibrary.data.projectTypeForms
      : [];
    const callForms = Array.isArray(formsLibrary.data?.callForms)
      ? formsLibrary.data.callForms
      : [];

    if (projectTypeIdFromUrl) {
      return (
        typeForms.find(
          (item: any) => Number(item?.projectTypeId) === projectTypeIdFromUrl,
        ) ?? null
      );
    }

    if (callIdFromUrl) {
      return (
        callForms.find((item: any) => Number(item?.callId) === callIdFromUrl) ??
        null
      );
    }

    if (preservedCallIdFromUrl) {
      return (
        callForms.find((item: any) => Number(item?.preservedCallId) === preservedCallIdFromUrl) ??
        null
      );
    }

    return null;
  }, [callIdFromUrl, formsLibrary.data, preservedCallIdFromUrl, projectTypeIdFromUrl]);

  useEffect(() => {
    setShowModelImpactWarning(true);
  }, [currentLibraryItem?.id]);

  useEffect(() => {
    setEditableCallProgramName(callProgramName ?? currentLibraryItem?.name ?? "");
  }, [callProgramName, currentLibraryItem?.name]);

  const allowAdvancedCallScoringModes = useMemo(() => {
    if ((currentLibraryItem as any)?.scope !== "call") {
      return true;
    }

    return true;
  }, [currentLibraryItem]);

  const fields = useMemo(() => {
    const stepData = steps.find((f) => f.step === selectedStep);
    const rootFields = stepData?.fields ?? [];
    const sectionFields = (stepData?.sections ?? []).flatMap(
      (section) => section.fields ?? [],
    );
    return [...rootFields, ...sectionFields];
  }, [steps, selectedStep]);
  const sections = useMemo(
    () => steps.find((f) => f.step === selectedStep)?.sections ?? [],
    [steps, selectedStep],
  );

  const normalizeKey = (value?: string | null) =>
    (value ?? "").trim().toLowerCase();
  const isIntroStep = (step?: Step) =>
    normalizeKey(step?.apiName) === INTRO_STEP_API_NAME ||
    normalizeKey(step?.nameStep) === INTRO_STEP_LABEL ||
    normalizeKey(step?.type) === "intro";
  const isProtectedStep = (step?: Step) =>
    normalizeKey(step?.apiName) === PROTECTED_STEP_API_NAME ||
    normalizeKey(step?.nameStep) === PROTECTED_STEP_LABEL;
  const isLockedFinancingStep = (step?: Step) =>
    normalizeKey(step?.apiName) === LOCKED_FINANCING_STEP_API_NAME ||
    normalizeKey(step?.nameStep) === LOCKED_FINANCING_STEP_LABEL;
  const isStepLockedForEdition = (step?: Step) =>
    isIntroStep(step) ||
    (!isCallSpecificEditor && isProtectedStep(step)) ||
    (!isCallSpecificEditor && isLockedFinancingStep(step));
  const selectedStepData = useMemo(
    () => steps.find((item) => item.step === selectedStep),
    [steps, selectedStep],
  );
  const currentCallFormScoringSettings = useMemo(() => {
    return getCallFormScoringSettings(steps);
  }, [allowAdvancedCallScoringModes, steps]);
  const hasMapFieldInForm = useMemo(
    () =>
      steps.some(
        (stepItem) =>
          stepItem.fields.some((field) => field.type === "Map") ||
          (stepItem.sections ?? []).some((section) =>
            (section.fields ?? []).some((field) => field.type === "Map"),
          ),
      ),
    [steps],
  );

  const hasFundingFieldInForm = useMemo(
    () =>
      steps.some(
        (stepItem) =>
          stepItem.fields.some(
            (field) =>
              field.type === "funding" ||
              normalizeKey(field.nativeField) === "desiredfunding",
          ) ||
          (stepItem.sections ?? []).some((section) =>
            (section.fields ?? []).some(
              (field) =>
                field.type === "funding" ||
                normalizeKey(field.nativeField) === "desiredfunding",
            ),
          ),
      ),
    [steps],
  );
  const findFieldById = (fieldId: number): Field | undefined => {
    if (!selectedStepData) return undefined;
    const rootField = selectedStepData.fields.find(
      (field) => field.id === fieldId,
    );
    if (rootField) return rootField;

    for (const section of selectedStepData.sections ?? []) {
      const sectionField = section.fields.find((field) => field.id === fieldId);
      if (sectionField) return sectionField;
    }

    return undefined;
  };
  const replaceOptimisticId = (serverId: number | undefined, optimisticId: number) => {
    if (!serverId || serverId === optimisticId) return;

    setSteps((currentSteps) =>
      currentSteps.map((step) => ({
        ...step,
        id: step.id === optimisticId ? serverId : step.id,
        fields: step.fields.map((field) => ({
          ...field,
          id: field.id === optimisticId ? serverId : field.id,
          parentId: field.parentId === optimisticId ? serverId : field.parentId,
          sectionId: field.sectionId === optimisticId ? serverId : field.sectionId,
        })),
        sections: (step.sections ?? []).map((section) => ({
          ...section,
          id: section.id === optimisticId ? serverId : section.id,
          fields: (section.fields ?? []).map((field) => ({
            ...field,
            id: field.id === optimisticId ? serverId : field.id,
            parentId: field.parentId === optimisticId ? serverId : field.parentId,
            sectionId: field.sectionId === optimisticId ? serverId : field.sectionId,
          })),
        })),
      })),
    );
  };
  const updateStepById = (
    stepId: number,
    updater: (step: Step) => Step,
  ) => {
    setSteps((currentSteps) =>
      currentSteps.map((step) => (step.id === stepId ? updater(step) : step)),
    );
  };
  const removeFieldFromSteps = (fieldId: number) => {
    setSteps((currentSteps) =>
      currentSteps.map((step) => ({
        ...step,
        fields: step.fields.filter((field) => field.id !== fieldId),
        sections: (step.sections ?? []).map((section) => ({
          ...section,
          fields: (section.fields ?? []).filter((field) => field.id !== fieldId),
        })),
      })),
    );
  };
  const upsertFieldInStep = (
    stepId: number,
    field: Field,
    mode: "create" | "update",
  ) => {
    setSteps((currentSteps) =>
      currentSteps.map((step) => {
        if (step.id !== stepId) return step;

        if (field.sectionId) {
          return {
            ...step,
            sections: (step.sections ?? []).map((section) => {
              if (section.id !== field.sectionId) return section;
              const nextFields =
                mode === "create"
                  ? [...(section.fields ?? []), field]
                  : (section.fields ?? []).map((item) =>
                      item.id === field.id ? { ...item, ...field } : item,
                    );

              return { ...section, fields: nextFields };
            }),
          };
        }

        return {
          ...step,
          fields:
            mode === "create"
              ? [...step.fields, field]
              : step.fields.map((item) =>
                  item.id === field.id ? { ...item, ...field } : item,
                ),
        };
      }),
    );
  };
  const rollbackSteps = (previousSteps: Step[], message: string) => {
    setSteps(previousSteps);
    toast({ title: message });
    setInlineError(message);
  };
  const flushPendingOptimisticChildren = async (optimisticParentId: number, serverParentId: number) => {
    const pendingSections = Object.values(pendingOptimisticSectionsRef.current)
      .filter((item) => item.parentId === optimisticParentId);

    for (const pending of pendingSections) {
      const sectionTempId = pending.section.id;
      if (!sectionTempId) continue;

      delete pendingOptimisticSectionsRef.current[sectionTempId];
      const result = await postData({
        url: appendScope(`${API_URL}/field_forms/save`),
        data: {
          isParent: false,
          parent: serverParentId,
          name: pending.section.name,
          label: pending.section.label,
          description: pending.section.description?.trim() ?? "",
          type: "section",
          step: pending.section.step,
          required: false,
          options: [],
        },
      });

      const sectionServerId = Number(result?.field?.id ?? 0) || undefined;
      if (sectionServerId) {
        replaceOptimisticId(sectionServerId, sectionTempId);
        await flushPendingOptimisticChildren(sectionTempId, sectionServerId);
      }
    }

    const pendingFields = Object.values(pendingOptimisticFieldsRef.current)
      .filter((item) => item.parentId === optimisticParentId);

    for (const pending of pendingFields) {
      const fieldTempId = pending.field.id;
      if (!fieldTempId) continue;

      delete pendingOptimisticFieldsRef.current[fieldTempId];
      const result = await postData({
        url: appendScope(`${API_URL}/field_forms/save`),
        data: {
          isParent: false,
          parent: serverParentId,
          name: pending.payload.name,
          label: pending.payload.label,
          type: pending.payload.type,
          step: pending.payload.step,
          required: pending.payload.required,
          options: buildOptionsWithVisibility(pending.payload.options, "all", []),
          nativeField: pending.payload.nativeField ?? null,
        },
      });

      replaceOptimisticId(Number(result?.field?.id ?? 0) || undefined, fieldTempId);
    }
  };
  const isProtectedDescriptifStep = isProtectedStep(selectedStepData);
  const isCallFormScoringActive =
    currentCallFormScoringSettings.enabled ||
    (scoringSettingsOpen && scoringSettingsDraft.enabled);

  useEffect(() => {
    if (!scoringSettingsOpen) return;
    setScoringSettingsDraft(currentCallFormScoringSettings);
  }, [currentCallFormScoringSettings, scoringSettingsOpen]);

  useEffect(() => {
    if (!allowAdvancedCallScoringModes) {
      setScoringSettingsDraft((current) => ({
        ...current,
        decisionMode: "auto_threshold",
      }));
    }
  }, [allowAdvancedCallScoringModes]);

  const ensureIntroductionStep = async (apiSteps: ApiStep[]) => {
    const hasIntro = apiSteps.some(
      (step) =>
        normalizeKey(step?.name) === INTRO_STEP_API_NAME ||
        normalizeKey(step?.label) === INTRO_STEP_LABEL ||
        normalizeKey(step?.type) === "intro",
    );
    if (hasIntro) return true;

    const result = await postData({
      url: appendScope(`${API_URL}/field_forms/save`),
      data: {
        isParent: true,
        name: INTRO_STEP_API_NAME,
        label: "Introduction",
        description: "",
        type: "intro",
        options: { description: "" },
        step: 1,
      },
    });
    return Boolean(result);
  };

  const buildFieldFormsGlobalUrl = () => {
    return appendScope(`${API_URL}/field-forms/global`);
  };

  const loadSteps = async (preferredStep?: number, preferredId?: number) => {
    setInlineError(null);
    const hasCachedSteps =
      steps.length > 0 ||
      typeof queryClient.getQueryData(fieldFormsGlobalQueryKey) !== "undefined";
    setLoadingSteps(!hasCachedSteps);
    const globalUrl = buildFieldFormsGlobalUrl();
    const fetchOptions = {
      queryKey: fieldFormsGlobalQueryKey,
      staleTime: FIELD_FORM_STEPS_CACHE_TTL_MS,
      gcTime: FIELD_FORM_STEPS_CACHE_TTL_MS,
    };
    const refreshCachedStepsInBackground = () => {
      if (!hasCachedSteps) return;

      void fetchData(globalUrl, {
        ...fetchOptions,
        force: true,
      }).then((freshResponse) => {
        const freshSteps = Array.isArray(freshResponse)
          ? (freshResponse as ApiStep[])
          : [];
        if (!freshSteps.length) return;
        const mappedFresh = normalizeStepsForUi(mapApiToSteps(freshSteps));
        if (!mappedFresh.length) return;
        setSteps(mappedFresh);
        if (!mappedFresh.some((step) => step.step === selectedStep)) {
          setSelectedStep(mappedFresh[0]?.step ?? 1);
        }
      });
    };

    try {
      const response = await fetchData(globalUrl, fetchOptions);
      let apiSteps = Array.isArray(response) ? (response as ApiStep[]) : [];

      if (apiSteps.length > 0) {
        const introReady = await ensureIntroductionStep(apiSteps);
        if (introReady) {
          const refreshed = await fetchData(globalUrl, {
            ...fetchOptions,
            force: true,
          });
          apiSteps = Array.isArray(refreshed) ? (refreshed as ApiStep[]) : apiSteps;
        }
      }

      if (!apiSteps.length) {
        if (!didTryInitializeRef.current) {
          didTryInitializeRef.current = true;
          const initRes = await postData({
            url: appendScope(`${API_URL}/field_forms/initialize`),
            data: buildInitializePayload(),
          });

          if (initRes) {
            const retry = await fetchData(globalUrl, {
              ...fetchOptions,
              force: true,
            });
            const retrySteps = Array.isArray(retry) ? (retry as ApiStep[]) : [];
            const mappedRetry = normalizeStepsForUi(mapApiToSteps(retrySteps));
            if (mappedRetry.length > 0) {
              setSteps(mappedRetry);
              setSelectedStep(mappedRetry[0]?.step ?? 1);
              refreshCachedStepsInBackground();
              return;
            }
          }
        }

        const message = "Impossible de charger le formulaire.";
        setInlineError(message);
        toast({ title: message });
        setSteps([]);
        return;
      }

      const mapped = normalizeStepsForUi(mapApiToSteps(apiSteps));
      setSteps(mapped);

      if (preferredId) {
        const foundById = mapped.find((s) => s.id === preferredId);
        if (foundById) {
          setSelectedStep(foundById.step);
          refreshCachedStepsInBackground();
          return;
        }
      }

      if (mapped.some((s) => s.step === selectedStep)) {
        refreshCachedStepsInBackground();
        return;
      }

      if (preferredStep && mapped.some((s) => s.step === preferredStep)) {
        setSelectedStep(preferredStep);
        refreshCachedStepsInBackground();
        return;
      }

      setSelectedStep(mapped[0]?.step ?? 1);
      refreshCachedStepsInBackground();
    } finally {
      setLoadingSteps(false);
    }
  };

  useEffect(() => {
    didTryInitializeRef.current = false;
    void loadSteps();
  }, [callIdFromUrl, preservedCallIdFromUrl, projectTypeIdFromUrl]);

  const handleReorderParents = async (nextSteps: Step[]) => {
    if (!canEditForm) {
      return false;
    }
    setInlineError(null);
    const fixedStepCount = nextSteps.filter(
      (step) => isIntroStep(step) || (!isCallSpecificEditor && isProtectedStep(step)),
    ).length;
    const movableSteps = [...nextSteps]
      .filter((s) => typeof s.id === "number")
      .filter((s) => !isIntroStep(s) && (isCallSpecificEditor || !isProtectedStep(s)))
      .sort((a, b) => a.step - b.step);

    const items = movableSteps.map((item, index) => ({
      id: item.id as number,
      step: fixedStepCount + 1 + index,
    }));

    if (!items.length) {
      return true;
    }

    const result = await postData({
      url: appendScope(`${API_URL}/field_forms/reorder`),
      data: { items },
    });

    if (!result?.success) {
      const message = "Réorganisation des étapes impossible.";
      toast({ title: message });
      setInlineError(message);
      return false;
    }

    return true;
  };

  const reorderSteps = (list: Step[], fromId: number, toId: number) => {
    const fromIndex = list.findIndex((s) => s.id === fromId);
    const toIndex = list.findIndex((s) => s.id === toId);
    if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) {
      return list;
    }

    const reordered = [...list];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, moved);

    return reordered.map((item, index) => ({
      ...item,
      step: index + 1,
    }));
  };

  const handleDropStep = async (targetStepId: number) => {
    const draggedStep = steps.find((step) => step.id === draggedStepId);
    const targetStep = steps.find((step) => step.id === targetStepId);
    if (
      !draggedStepId ||
      draggedStepId === targetStepId ||
      isBusy ||
      !canEditForm ||
      isIntroStep(draggedStep) ||
      isIntroStep(targetStep) ||
      (!isCallSpecificEditor && isProtectedStep(draggedStep)) ||
      (!isCallSpecificEditor && isProtectedStep(targetStep))
    ) {
      setDraggedStepId(null);
      setDragOverStepId(null);
      return;
    }

    const selectedId = selectedStepData?.id;
    const nextSteps = reorderSteps(steps, draggedStepId, targetStepId);
    setSteps(nextSteps);

    if (selectedId) {
      const selectedAfterMove = nextSteps.find((s) => s.id === selectedId);
      if (selectedAfterMove) {
        setSelectedStep(selectedAfterMove.step);
      }
    }

    setDraggedStepId(null);
    setDragOverStepId(null);

    const success = await handleReorderParents(nextSteps);
    if (!success) {
      await loadSteps(undefined, selectedId);
      return;
    }
    // Keep optimistic order in UI; avoid immediate refetch that can reintroduce stale ordering.
  };

  const handleDeleteStep = async (stepToDelete: Step) => {
    if (!canEditForm) {
      return;
    }
    setInlineError(null);
    if (!isCallSpecificEditor && isProtectedStep(stepToDelete)) {
      const message = "L'étape « Descriptif du projet » ne peut pas être supprimée.";
      toast({ title: message });
      setInlineError(message);
      return;
    }
    if (isIntroStep(stepToDelete)) {
      const message = "L'étape « Introduction » ne peut pas être supprimée.";
      toast({ title: message });
      setInlineError(message);
      return;
    }
    if (!isCallSpecificEditor && isLockedFinancingStep(stepToDelete)) {
      const message = "L'étape « Financement » ne peut pas être supprimée.";
      toast({ title: message });
      setInlineError(message);
      return;
    }

    if (!stepToDelete.id) {
      return;
    }

    const previousSteps = steps;
    const remaining = steps.filter((s) => s.id !== stepToDelete.id);
    const fallbackStep = remaining[0]?.step ?? 1;
    setSteps(remaining);
    setSelectedStep(fallbackStep);

    const result = await postData({
      url: appendScope(`${API_URL}/field_forms/delete/${stepToDelete.id}`),
      data: {},
    });

    if (!result?.success) {
      rollbackSteps(previousSteps, "Suppression de l'étape impossible.");
      return;
    }

    await handleReorderParents(remaining);
  };

  const handleAddStep = async () => {
    if (!canEditForm) {
      return;
    }
    setInlineError(null);
    const nextStepNumber = steps.length
      ? Math.max(...steps.map((s) => s.step)) + 1
      : 1;

    const defaultLabel = "Nouvelle étape";
    const previousSteps = steps;
    const optimisticId = -Date.now();
    const optimisticStep: Step = {
      id: optimisticId,
      step: nextStepNumber,
      nameStep: defaultLabel,
      description: "",
      apiName: toApiStepName(defaultLabel, nextStepNumber),
      type: "step",
      fields: [],
      sections: [],
    };
    setSteps((currentSteps) => normalizeStepsForUi([...currentSteps, optimisticStep]));
    setSelectedStep(nextStepNumber);
    setEditStep(nextStepNumber);
    setStepLabelDraft(defaultLabel);

    const result = await postData({
      url: appendScope(`${API_URL}/field_forms/save`),
      data: {
        isParent: true,
        name: toApiStepName(defaultLabel, nextStepNumber),
        label: defaultLabel,
        description: "",
        type: "step",
        step: nextStepNumber,
      },
    });

    if (!result) {
      rollbackSteps(previousSteps, "Création de l'étape impossible.");
      return;
    }

    const serverId = Number(result?.field?.id ?? 0) || undefined;
    replaceOptimisticId(serverId, optimisticId);

    const pendingLabel = pendingOptimisticStepLabelsRef.current[optimisticId]?.trim();
    delete pendingOptimisticStepLabelsRef.current[optimisticId];

    if (serverId && pendingLabel && pendingLabel !== defaultLabel) {
      const persistLabelResult = await postData({
        url: appendScope(`${API_URL}/field_forms/save`),
        data: {
          id: serverId,
          isParent: true,
          name: toApiStepName(defaultLabel, nextStepNumber),
          label: pendingLabel,
          description: "",
          type: "step",
          step: nextStepNumber,
          options: {
            visibilityMode: "all",
            visibilityProjectTypeIds: [],
          },
        },
      });

      if (!persistLabelResult) {
        rollbackSteps(previousSteps, "Modification de l'étape impossible.");
      }
    }

    if (serverId) {
      await flushPendingOptimisticChildren(optimisticId, serverId);
    }
  };

  const handleSaveStepLabel = async (stepItem: Step) => {
    if (!canEditForm) {
      return;
    }
    setInlineError(null);
    if (isStepLockedForEdition(stepItem)) {
      return;
    }
    const safeLabel = stepLabelDraft.trim();
    if (!safeLabel || !stepItem.id) {
      setEditStep(-1);
      setStepLabelDraft("");
      return;
    }

    const previousSteps = steps;
    updateStepById(stepItem.id, (step) => ({
      ...step,
      nameStep: safeLabel,
    }));
    setEditStep(-1);
    setStepLabelDraft("");

    if (stepItem.id < 0) {
      pendingOptimisticStepLabelsRef.current[stepItem.id] = safeLabel;
      return;
    }

    const result = await postData({
      url: appendScope(`${API_URL}/field_forms/save`),
      data: {
        id: stepItem.id,
        isParent: true,
        name:
          stepItem.apiName ?? toApiStepName(stepItem.nameStep, stepItem.step),
        label: safeLabel,
        description: stepItem.description?.trim() ?? "",
        type: "step",
        step: stepItem.step,
        options: {
          visibilityMode: stepItem.visibilityMode ?? "all",
          visibilityProjectTypeIds: stepItem.visibilityProjectTypeIds ?? [],
        },
      },
    });

    if (!result) {
      rollbackSteps(previousSteps, "Modification de l'étape impossible.");
      setEditStep(stepItem.step);
      setStepLabelDraft(stepItem.nameStep);
      return;
    }
  };

  const handleSaveSelectedStepDescription = async (description: string) => {
    setInlineError(null);
    if (
      !selectedStepData ||
      (!isCallSpecificEditor && isProtectedStep(selectedStepData)) ||
      (!isCallSpecificEditor && isLockedFinancingStep(selectedStepData))
    ) {
      return;
    }
    if (!selectedStepData.id) {
      return;
    }

    const previousSteps = steps;
    updateStepById(selectedStepData.id, (step) => ({
      ...step,
      description: description.trim(),
      options: isIntroStep(step)
        ? { ...(step.options ?? {}), description: description.trim() }
        : step.options,
    }));

    const result = await postData({
      url: appendScope(`${API_URL}/field_forms/save`),
      data: {
        id: selectedStepData.id,
        isParent: true,
        name: isIntroStep(selectedStepData)
          ? INTRO_STEP_API_NAME
          : (selectedStepData.apiName ??
            toApiStepName(selectedStepData.nameStep, selectedStepData.step)),
        label: isIntroStep(selectedStepData)
          ? "Introduction"
          : selectedStepData.nameStep,
        description: description.trim(),
        type: isIntroStep(selectedStepData) ? "intro" : "step",
        options: isIntroStep(selectedStepData)
          ? { description: description.trim() }
          : {
              visibilityMode: selectedStepData.visibilityMode ?? "all",
              visibilityProjectTypeIds:
                selectedStepData.visibilityProjectTypeIds ?? [],
            },
        step: selectedStepData.step,
      },
    });

    if (!result) {
      rollbackSteps(previousSteps, "Modification de la description de l'étape impossible.");
      return;
    }
  };

  const handleCreateField = async (payload: UpsertFieldPayload) => {
    if (!canEditForm) {
      return;
    }
    setInlineError(null);
    if (!selectedStepData?.id) {
      const message = "Étape invalide.";
      toast({ title: message });
      setInlineError(message);
      return;
    }
    if (payload.type === "Map" && hasMapFieldInForm) {
      const message =
        "Un seul champ « Carte » est autorisé pour l'ensemble du formulaire.";
      toast({ title: message });
      setInlineError(message);
      return;
    }
    if (isCallSpecificEditor && payload.type === "funding" && hasFundingFieldInForm) {
      const message =
        "Un seul champ Financement est autorise pour l'ensemble du formulaire.";
      toast({ title: message });
      setInlineError(message);
      return;
    }

    const previousSteps = steps;
    const optimisticId = -Date.now();
    const optimisticField: Field = {
      id: optimisticId,
      name: payload.name,
      label: payload.label,
      type: payload.type,
      required: payload.required,
      options: extractChoiceOptions(payload.options),
      scoring: extractScoringConfig(payload.options),
      decisionRules: extractDecisionRulesConfig(payload.options),
      visibilityMode: "all",
      visibilityProjectTypeIds: [],
      nativeField: payload.nativeField,
      step: payload.step,
      parentId: payload.parentId ?? selectedStepData.id,
      sectionId:
        payload.parentId && payload.parentId !== selectedStepData.id
          ? payload.parentId
          : null,
    };
    upsertFieldInStep(selectedStepData.id, optimisticField, "create");

    const parentId = payload.parentId ?? selectedStepData.id;
    if (parentId < 0) {
      pendingOptimisticFieldsRef.current[optimisticId] = {
        parentId,
        rootStepId: selectedStepData.id,
        field: optimisticField,
        payload,
      };
      return;
    }

    const result = await postData({
      url: appendScope(`${API_URL}/field_forms/save`),
      data: {
        isParent: false,
        parent: parentId,
        name: payload.name,
        label: payload.label,
        type: payload.type,
        step: payload.step,
        required: payload.required,
        options: buildOptionsWithVisibility(payload.options, "all", []),
        nativeField: payload.nativeField ?? null,
      },
    });

    if (!result) {
      rollbackSteps(previousSteps, "Création du champ impossible.");
      return;
    }

    replaceOptimisticId(Number(result?.field?.id ?? 0) || undefined, optimisticId);
  };

  const handleUpdateField = async (
    fieldId: number,
    payload: UpsertFieldPayload,
  ) => {
    if (!canEditForm) {
      return;
    }
    setInlineError(null);
    if (!selectedStepData?.id) {
      const message = "Étape invalide.";
      toast({ title: message });
      setInlineError(message);
      return;
    }

    const existingField = findFieldById(fieldId);
    if (
      payload.type === "Map" &&
      existingField?.type !== "Map" &&
      hasMapFieldInForm
    ) {
      const message =
        "Un seul champ « Carte » est autorisé pour l'ensemble du formulaire.";
      toast({ title: message });
      setInlineError(message);
      return;
    }
    if (
      isCallSpecificEditor &&
      payload.type === "funding" &&
      existingField?.type !== "funding" &&
      hasFundingFieldInForm
    ) {
      const message =
        "Un seul champ Financement est autorise pour l'ensemble du formulaire.";
      toast({ title: message });
      setInlineError(message);
      return;
    }
    const previousSteps = steps;
    const updatedField: Field = {
      ...(existingField ?? {
        id: fieldId,
        name: payload.name,
        label: payload.label,
        type: payload.type,
        required: payload.required,
        step: payload.step,
      }),
      id: fieldId,
      name: payload.name,
      label: payload.label,
      type: payload.type,
      required: payload.required,
      options: extractChoiceOptions(payload.options),
      scoring: extractScoringConfig(payload.options),
      decisionRules: extractDecisionRulesConfig(payload.options),
      nativeField: payload.nativeField,
      step: payload.step,
      parentId: payload.parentId ?? existingField?.parentId ?? selectedStepData.id,
      sectionId:
        payload.parentId && payload.parentId !== selectedStepData.id
          ? payload.parentId
          : (existingField?.sectionId ?? null),
    };
    upsertFieldInStep(selectedStepData.id, updatedField, "update");

    const parentId =
      payload.parentId ?? existingField?.parentId ?? selectedStepData.id;
    if (fieldId < 0 || parentId < 0) {
      pendingOptimisticFieldsRef.current[fieldId] = {
        parentId,
        rootStepId: selectedStepData.id,
        field: updatedField,
        payload,
      };
      return;
    }

    const result = await postData({
      url: appendScope(`${API_URL}/field_forms/save`),
      data: {
        id: fieldId,
        isParent: false,
        parent: parentId,
        name: payload.name,
        label: payload.label,
        type: payload.type,
        step: payload.step,
        required: payload.required,
        options: buildOptionsWithVisibility(
          payload.options,
          existingField?.visibilityMode ?? "all",
          existingField?.visibilityProjectTypeIds ?? [],
        ),
        nativeField: payload.nativeField ?? null,
      },
    });

    if (!result) {
      rollbackSteps(previousSteps, "Modification du champ impossible.");
      return;
    }
  };

  const handleDeleteField = async (fieldId: number) => {
    if (!canEditForm) {
      return;
    }
    if (isProtectedDescriptifStep) {
      const field = findFieldById(fieldId);
      const nativeKey = normalizeKey(field?.nativeField);
      if (nativeKey && PROTECTED_DESCRIPTIF_FIELDS.has(nativeKey)) {
        const message = "Ce champ est obligatoire dans l'étape « Descriptif du projet ».";
        toast({ title: message });
        setInlineError(message);
        return;
      }
    }

    setInlineError(null);
    const previousSteps = steps;
    removeFieldFromSteps(fieldId);
    if (fieldId < 0) {
      delete pendingOptimisticFieldsRef.current[fieldId];
      return;
    }

    const result = await postData({
      url: appendScope(`${API_URL}/field_forms/delete/${fieldId}`),
      data: {},
    });

    if (!result?.success) {
      rollbackSteps(previousSteps, "Suppression du champ impossible.");
      return;
    }
  };

  const handleCreateSection = async (title: string, description: string) => {
    if (!canEditForm) {
      return;
    }
    setInlineError(null);
    if (!selectedStepData?.id) {
      const message = "Étape invalide.";
      toast({ title: message });
      setInlineError(message);
      return;
    }

    const maxChildStep = Math.max(
      0,
      ...selectedStepData.fields.map((f) => f.step ?? 0),
      ...(selectedStepData.sections ?? []).map((s) => s.step ?? 0),
    );

    const safeTitle = title.trim();
    if (!safeTitle) {
      return;
    }

    const previousSteps = steps;
    const optimisticId = -Date.now();
    const optimisticSection: Section = {
      id: optimisticId,
      name: toApiStepName(safeTitle, maxChildStep + 1),
      label: safeTitle,
      description: description.trim(),
      step: maxChildStep + 1,
      fields: [],
    };
    updateStepById(selectedStepData.id, (step) => ({
      ...step,
      sections: [...(step.sections ?? []), optimisticSection],
    }));

    if (selectedStepData.id < 0) {
      pendingOptimisticSectionsRef.current[optimisticId] = {
        parentId: selectedStepData.id,
        section: optimisticSection,
      };
      return;
    }

    const result = await postData({
      url: appendScope(`${API_URL}/field_forms/save`),
      data: {
        isParent: false,
        parent: selectedStepData.id,
        name: toApiStepName(safeTitle, maxChildStep + 1),
        label: safeTitle,
        description: description.trim(),
        type: "section",
        step: maxChildStep + 1,
        required: false,
        options: [],
      },
    });

    if (!result) {
      rollbackSteps(previousSteps, "Création de la section impossible.");
      return;
    }

    const serverId = Number(result?.field?.id ?? 0) || undefined;
    replaceOptimisticId(serverId, optimisticId);
    if (serverId) {
      await flushPendingOptimisticChildren(optimisticId, serverId);
    }
  };

  const handleUpdateSection = async (
    sectionId: number,
    title: string,
    description: string,
  ) => {
    if (!canEditForm) {
      return;
    }
    setInlineError(null);
    if (!selectedStepData?.id) {
      const message = "Étape invalide.";
      toast({ title: message });
      setInlineError(message);
      return;
    }

    const section = (selectedStepData.sections ?? []).find(
      (item) => item.id === sectionId,
    );
    if (!section?.id) {
      return;
    }

    const safeTitle = title.trim();
    if (!safeTitle) {
      return;
    }

    const previousSteps = steps;
    updateStepById(selectedStepData.id, (step) => ({
      ...step,
      sections: (step.sections ?? []).map((item) =>
        item.id === section.id
          ? {
              ...item,
              label: safeTitle,
              description: description.trim(),
            }
          : item,
      ),
    }));

    if (section.id < 0) {
      pendingOptimisticSectionsRef.current[section.id] = {
        parentId: selectedStepData.id,
        section: {
          ...section,
          label: safeTitle,
          description: description.trim(),
        },
      };
      return;
    }

    const result = await postData({
      url: appendScope(`${API_URL}/field_forms/save`),
      data: {
        id: section.id,
        isParent: false,
        parent: selectedStepData.id,
        name: section.name ?? toApiStepName(safeTitle, section.step),
        label: safeTitle,
        description: description.trim(),
        type: "section",
        step: section.step,
        required: false,
        options: [],
      },
    });

    if (!result) {
      rollbackSteps(previousSteps, "Modification de la section impossible.");
      return;
    }
  };

  const handleDeleteSection = async (sectionId: number) => {
    if (!canEditForm) {
      return;
    }
    setInlineError(null);
    const previousSteps = steps;
    setSteps((currentSteps) =>
      currentSteps.map((step) => ({
        ...step,
        sections: (step.sections ?? []).filter((section) => section.id !== sectionId),
      })),
    );
    if (sectionId < 0) {
      delete pendingOptimisticSectionsRef.current[sectionId];
      Object.entries(pendingOptimisticFieldsRef.current).forEach(([fieldId, pending]) => {
        if (pending.parentId === sectionId) {
          delete pendingOptimisticFieldsRef.current[Number(fieldId)];
        }
      });
      return;
    }

    const result = await postData({
      url: appendScope(`${API_URL}/field_forms/delete/${sectionId}`),
      data: {},
    });

    if (!result?.success) {
      rollbackSteps(previousSteps, "Suppression de la section impossible.");
      return;
    }
  };

  const handleUpdateStepVisibility = async (
    stepItem: Step,
    mode: "all" | "show_only" | "hide_only",
    projectTypeIds: number[],
  ) => {
    if (!canEditForm || !stepItem.id) return;
    setInlineError(null);
    const previousSteps = steps;
    updateStepById(stepItem.id, (step) => ({
      ...step,
      visibilityMode: mode,
      visibilityProjectTypeIds: projectTypeIds,
    }));

    const result = await postData({
      url: appendScope(`${API_URL}/field_forms/save`),
      data: {
        id: stepItem.id,
        isParent: true,
        name: isIntroStep(stepItem)
          ? INTRO_STEP_API_NAME
          : (stepItem.apiName ?? toApiStepName(stepItem.nameStep, stepItem.step)),
        label: isIntroStep(stepItem) ? "Introduction" : stepItem.nameStep,
        description: stepItem.description?.trim() ?? "",
        type: isIntroStep(stepItem) ? "intro" : "step",
        step: stepItem.step,
        options: {
          visibilityMode: mode,
          visibilityProjectTypeIds: projectTypeIds,
        },
      },
    });

    if (!result) {
      rollbackSteps(previousSteps, "Mise à jour des paramètres de l'étape impossible.");
      return;
    }
  };

  const handleUpdateFieldVisibility = async (
    field: Field,
    mode: "all" | "show_only" | "hide_only",
    projectTypeIds: number[],
  ) => {
    if (!canEditForm || !field.id) return;
    setInlineError(null);
    const parentId = field.parentId ?? selectedStepData?.id;
    if (!parentId) return;
    const previousSteps = steps;
    const updatedField: Field = {
      ...field,
      visibilityMode: mode,
      visibilityProjectTypeIds: projectTypeIds,
    };
    if (selectedStepData?.id) {
      upsertFieldInStep(selectedStepData.id, updatedField, "update");
    }
    if (field.id < 0 || parentId < 0) {
      const pending = pendingOptimisticFieldsRef.current[field.id];
      if (pending) {
        pendingOptimisticFieldsRef.current[field.id] = {
          ...pending,
          parentId,
          field: updatedField,
          payload: {
            ...pending.payload,
            options: buildOptionsWithVisibility(field.options, mode, projectTypeIds),
          },
        };
      }
      return;
    }

    const result = await postData({
      url: appendScope(`${API_URL}/field_forms/save`),
      data: {
        id: field.id,
        isParent: false,
        parent: parentId,
        name: field.name,
        label: field.label,
        type: field.type,
        step: field.step,
        required: field.required,
        options: buildOptionsWithVisibility(field.options, mode, projectTypeIds),
        nativeField: field.nativeField ?? null,
      },
    });

    if (!result) {
      rollbackSteps(previousSteps, "Mise à jour des paramètres du champ impossible.");
      return;
    }
  };

  const scoringSettingsNeedRejectionThreshold =
    scoringSettingsDraft.decisionMode === "auto_threshold" ||
    scoringSettingsDraft.decisionMode === "auto_total" ||
    scoringSettingsDraft.decisionMode === "manual_total";
  const scoringSettingsNeedAcceptanceThreshold =
    scoringSettingsDraft.decisionMode === "auto_threshold" ||
    scoringSettingsDraft.decisionMode === "auto_total";
  const parsedAcceptanceThreshold =
    scoringSettingsDraft.acceptancePercentage !== null &&
    scoringSettingsDraft.acceptancePercentage !== undefined
      ? Number(scoringSettingsDraft.acceptancePercentage)
      : NaN;
  const parsedRejectionThreshold =
    scoringSettingsDraft.rejectionPercentage !== null &&
    scoringSettingsDraft.rejectionPercentage !== undefined
      ? Number(scoringSettingsDraft.rejectionPercentage)
      : NaN;
  const isScoringSettingsValid =
    !scoringSettingsDraft.enabled ||
    (Boolean(scoringSettingsDraft.decisionMode) &&
      (!scoringSettingsNeedRejectionThreshold ||
        (Number.isFinite(parsedRejectionThreshold) &&
          parsedRejectionThreshold >= 0 &&
          parsedRejectionThreshold <= 100 &&
          (!scoringSettingsNeedAcceptanceThreshold ||
            (Number.isFinite(parsedAcceptanceThreshold) &&
              parsedAcceptanceThreshold >= 0 &&
              parsedAcceptanceThreshold <= 100 &&
              parsedRejectionThreshold <= parsedAcceptanceThreshold)))));

  const saveCallFormScoringSettings = async (
    settings: CallFormScoringSettings | null,
  ) => {
    const introStep = steps.find((step) => isIntroStep(step));
    if (!introStep?.id) {
      return false;
    }

    const baseOptions =
      introStep.options &&
      typeof introStep.options === "object" &&
      !Array.isArray(introStep.options)
        ? { ...(introStep.options as Record<string, unknown>) }
        : {};

    if (settings) {
      baseOptions[CALL_FORM_SCORING_SETTINGS_KEY] = settings;
    } else {
      baseOptions[CALL_FORM_SCORING_SETTINGS_KEY] = null;
    }

    const previousSteps = steps;
    updateStepById(introStep.id, (step) => ({
      ...step,
      options: baseOptions,
    }));

    const result = await postData({
      url: appendScope(`${API_URL}/field_forms/save`),
      data: {
        id: introStep.id,
        isParent: true,
        name: INTRO_STEP_API_NAME,
        label: "Introduction",
        description: introStep.description?.trim() ?? "",
        type: "intro",
        options: baseOptions,
        step: introStep.step,
      },
    });

    if (!result) {
      rollbackSteps(previousSteps, "Mise à jour du système de notation impossible.");
      return false;
    }

    return true;
  };

  const handleToggleCallFormScoring = async (enabled: boolean) => {
    if (!canEditForm) return;
    setInlineError(null);

    if (!enabled) {
      setScoringSettingsOpen(false);
      setScoringSettingsDraft({ enabled: false });
      const success = await saveCallFormScoringSettings(null);
      if (!success) {
        setScoringSettingsDraft(currentCallFormScoringSettings);
      }
      return;
    }

    setScoringSettingsDraft((current) => ({
      enabled: true,
      decisionMode:
        (allowAdvancedCallScoringModes
          ? current.decisionMode
          : "auto_threshold") ??
        currentCallFormScoringSettings.decisionMode ??
        (allowAdvancedCallScoringModes ? undefined : "auto_threshold"),
      acceptancePercentage:
        current.acceptancePercentage ??
        currentCallFormScoringSettings.acceptancePercentage ??
        null,
      rejectionPercentage:
        current.rejectionPercentage ??
        currentCallFormScoringSettings.rejectionPercentage ??
        null,
    }));
    setScoringSettingsOpen(true);
  };

  const handleSaveCallFormScoringSettings = async () => {
    if (!isScoringSettingsValid) {
      return;
    }

    const nextSettings: CallFormScoringSettings = {
      enabled: true,
      decisionMode: allowAdvancedCallScoringModes
        ? scoringSettingsDraft.decisionMode
        : "auto_threshold",
      acceptancePercentage: scoringSettingsNeedAcceptanceThreshold
        ? parsedAcceptanceThreshold
        : null,
      rejectionPercentage: scoringSettingsNeedRejectionThreshold
        ? parsedRejectionThreshold
        : null,
    };

    setScoringSettingsOpen(false);
    setScoringSettingsDraft(nextSettings);

    const success = await saveCallFormScoringSettings({
      ...nextSettings,
    });

    if (!success) {
      setScoringSettingsOpen(true);
    }
  };

  const isBusy = loadingSteps || loadingMutation;
  const displayedCallProgramName = useMemo(
    () =>
      editableCallProgramName.trim() ||
      callProgramName ||
      currentLibraryItem?.name ||
      "Formulaire d'appel à projets",
    [editableCallProgramName, callProgramName, currentLibraryItem?.name],
  );
  const handleSaveCallProgramName = async () => {
    if (!callIdFromUrl) return;

    const nextName = editableCallProgramName.trim();
    if (!nextName) {
      setInlineError("Le nom du programme est obligatoire.");
      return;
    }

    setInlineError(null);
    setSavingCallProgramName(true);

    const result = await postData({
      url: `${API_URL}/update-call-for-project`,
      data: {
        idCall: callIdFromUrl,
        newProgramName: nextName,
      },
    });

    setSavingCallProgramName(false);

    if (!(result as any)?.success) {
      setInlineError("Impossible d'enregistrer le nom du programme.");
      return;
    }

    const params = new URLSearchParams(searchParams);
    params.set("programName", nextName);
    navigate(
      {
        pathname: "/dashboard/parameters",
        search: `?${params.toString()}`,
      },
      { replace: true },
    );
    setIsEditingCallProgramName(false);
  };

  const openDeleteModal = async () => {
    if (!currentLibraryItem) {
      return;
    }

    setCheckingDeleteUsage(true);
    let resolvedItem: any = currentLibraryItem;

    try {
      const query = new URLSearchParams();
      if (projectTypeIdFromUrl) {
        query.set("projectTypeId", String(projectTypeIdFromUrl));
      } else if (callIdFromUrl) {
        query.set("callId", String(callIdFromUrl));
      } else if (preservedCallIdFromUrl) {
        query.set("preservedCallId", String(preservedCallIdFromUrl));
      }
      const usage = await fetchData(`${API_URL}/field-forms/usage?${query.toString()}`);
      resolvedItem = {
        ...currentLibraryItem,
        usageCount: Number(usage?.usageCount ?? currentLibraryItem?.usageCount ?? 0),
        canDelete:
          typeof usage?.canDelete === "boolean"
            ? usage.canDelete
            : currentLibraryItem?.canDelete,
      };
    } catch (_error) {
      resolvedItem = currentLibraryItem;
    } finally {
      setCheckingDeleteUsage(false);
    }

    if ((resolvedItem?.usageCount ?? 0) > 0 || resolvedItem?.canDelete === false) {
      setBlockedDeleteDialogOpen(true);
      return;
    }

    setDeleteDialogOpen(true);
  };

  const handleConfirmDeleteCurrentForm = async () => {
    setDeletingForm(true);
    setInlineError(null);

    try {
      await postData({
        url: appendScope(`${API_URL}/field_forms/delete-model`),
        data: {},
      });

      setDeleteDialogOpen(false);
      navigate("/dashboard/parameters?params=2");
    } catch (error) {
      const message = "Suppression du formulaire impossible.";
      toast({ title: message });
      setInlineError(message);
    } finally {
      setDeletingForm(false);
    }
  };

  return (
    <div className="w-full">
      <div className="my-5 space-y-4">
        <div className="flex flex-wrap items-center gap-2 text-sm text-slate-500">
          <Link
            to="/dashboard/parameters?params=2"
            className="transition-colors hover:text-emerald-700"
          >
            Mes formulaires
          </Link>
          <ChevronRight className="h-4 w-4 text-slate-400" />
          <span className="font-medium text-slate-900">
            {isCallSpecificEditor
              ? displayedCallProgramName
              : isProjectTypeEditor
                ? (projectTypeName || "Formulaire par type de projet")
                : "Édition"}
          </span>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-5">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate("/dashboard/parameters?params=2")}
              className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-sm transition-colors hover:border-emerald-300 hover:text-emerald-700"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <div>
              {callIdFromUrl && isEditingCallProgramName ? (
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    id="program-title"
                    type="text"
                    value={editableCallProgramName}
                    onChange={(e) => setEditableCallProgramName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        void handleSaveCallProgramName();
                      }
                      if (e.key === "Escape") {
                        setEditableCallProgramName(callProgramName ?? currentLibraryItem?.name ?? "");
                        setIsEditingCallProgramName(false);
                        setInlineError(null);
                      }
                    }}
                    className="h-11 min-w-[320px] max-w-[680px] rounded-xl border border-slate-300 bg-white px-3 text-[24px] font-semibold tracking-tight text-slate-900"
                    disabled={savingCallProgramName}
                    autoFocus
                  />
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => void handleSaveCallProgramName()}
                    disabled={savingCallProgramName}
                  >
                    {savingCallProgramName ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      "Enregistrer"
                    )}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setEditableCallProgramName(callProgramName ?? currentLibraryItem?.name ?? "");
                      setIsEditingCallProgramName(false);
                      setInlineError(null);
                    }}
                    disabled={savingCallProgramName}
                  >
                    Annuler
                  </Button>
                </div>
              ) : (
                <div className="flex flex-wrap items-center gap-2">
                  <h1
                    className="inline-block text-[24px] font-semibold tracking-tight"
                    id="program-title"
                  >
                    {isCallSpecificEditor
                      ? displayedCallProgramName
                      : isProjectTypeEditor
                        ? (projectTypeName || "Formulaire par type de projet")
                        : "Mes formulaires"}
                  </h1>
                  {callIdFromUrl && canEditForm && (
                    <button
                      type="button"
                      onClick={() => {
                        setEditableCallProgramName(displayedCallProgramName);
                        setIsEditingCallProgramName(true);
                        setInlineError(null);
                      }}
                      className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 transition-colors hover:border-emerald-300 hover:text-emerald-700"
                      title="Modifier le nom du programme"
                    >
                      <SquarePen className="h-4 w-4" />
                    </button>
                  )}
                </div>
              )}
              <p className="mt-1 text-sm text-slate-500">
                {isCallSpecificEditor
                  ? "Formulaire d'appel à projets"
                  : isProjectTypeEditor
                    ? "Formulaire par type de projet"
                    : "Bibliothèque des formulaires"}
              </p>
            </div>
          </div>
          {currentLibraryItem && (
            <button
              type="button"
              onClick={() => void openDeleteModal()}
              disabled={checkingDeleteUsage}
              className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-red-200 bg-white text-red-600 shadow-sm transition-colors hover:bg-red-50"
              title="Supprimer le formulaire"
            >
              {checkingDeleteUsage ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <Trash className="h-5 w-5" />
              )}
            </button>
          )}
        </div>
      </div>
      {showGlobalCallWarning && (
        <div className="mb-5 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <AlertTriangle className="mt-0.5 h-5 w-5 text-amber-500" />
          <p className="m-0">
            Attention : vous utilisez le formulaire global
            {(callProgramName ?? currentLibraryItem?.name)
              ? ` pour le programme "${callProgramName ?? currentLibraryItem?.name}"`
              : ""}.
            Toute modification effectuée ici impactera également le formulaire
            global utilisé pour la création de projet et de vos autres appels à projet .
          </p>
        </div>
      )}
      {currentLibraryItem && showModelImpactWarning && (
        <div className="mb-5 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <AlertTriangle className="mt-0.5 h-5 w-5 text-amber-500" />
          <p className="m-0 leading-6">
            Attention : toute modification apportée à ce formulaire peut avoir
            un impact sur les projets ou appels à projets qui utilisent ce
            modèle.
            {(currentLibraryItem.usageCount ?? 0) > 0 && (
              <>
                {" "}
                Ce modèle est actuellement utilisé par un ou plusieurs éléments
                actifs.
              </>
            )}
          </p>
          <button
            type="button"
            onClick={() => setShowModelImpactWarning(false)}
            className="ml-auto inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-amber-700 transition-colors hover:bg-amber-100 hover:text-amber-900"
            aria-label="Fermer l'avertissement"
            title="Fermer"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
      {isCallSpecificEditor && (
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4">
          <div className="min-w-0">
            <p className="mb-1 text-sm font-semibold text-slate-800">
              Système de notation du formulaire
            </p>
            <p className="text-xs text-slate-600">
              Activez-le si vous souhaitez automatiser la validation, le rejet
              ou encadrer l&apos;évaluation finale des candidatures.
            </p>
            {currentCallFormScoringSettings.enabled &&
            currentCallFormScoringSettings.decisionMode ? (
              <p className="mt-2 text-xs font-medium text-[#1d5214]">
                {currentCallFormScoringSettings.decisionMode === "manual_total"
                  ? `Décision finale laissée au porteur de projet, avec rejet automatique jusqu'à ${currentCallFormScoringSettings.rejectionPercentage ?? 0}%.`
                  : `Validation automatique à partir de ${currentCallFormScoringSettings.acceptancePercentage ?? 0}% et rejet automatique jusqu'à ${currentCallFormScoringSettings.rejectionPercentage ?? 0}%.`}
              </p>
            ) : null}
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-3">
              <Switch
                id="call-form-scoring-enabled"
                checked={isCallFormScoringActive}
                onCheckedChange={(value: boolean) => void handleToggleCallFormScoring(value)}
                disabled={!canEditForm || loadingSteps}
              />
              <label
                htmlFor="call-form-scoring-enabled"
                className="m-0 text-sm font-medium text-slate-700"
              >
                Activer la notation
              </label>
            </div>
            {isCallFormScoringActive && (
              <Button
                type="button"
                variant="outline"
                className="gap-2"
                onClick={() => {
                  setScoringSettingsDraft(currentCallFormScoringSettings);
                  setScoringSettingsOpen(true);
                }}
                disabled={!canEditForm || isBusy}
              >
                <Settings2 className="h-4 w-4" />
                Paramétrer
              </Button>
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-[minmax(290px,340px)_minmax(0,1fr)] gap-6">
        <ScrollArea className="h-[72vh] rounded-2xl border border-slate-200 bg-slate-50 p-3">
          <div className="mb-3 px-2 pt-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Étapes du formulaire
            </p>
            <p className="text-sm text-slate-600">
              {steps.length} étapes configurées
            </p>
          </div>

          {steps.map((item) => (
            <button
              key={item.id ?? item.step}
              className={`mb-2 flex w-full items-center gap-3 rounded-xl border p-3 text-left transition-all ${
                selectedStep === item.step
                  ? "border-emerald-300 bg-emerald-50 shadow-sm"
                  : "border-transparent bg-white hover:border-slate-200 hover:bg-slate-100"
              } ${
                dragOverStepId === item.id
                  ? "border-emerald-400 ring-2 ring-emerald-200"
                  : ""
              }`}
              onClick={() => setSelectedStep(item.step)}
              type="button"
              draggable={
                Boolean(item.id) &&
                !isBusy &&
                canEditForm &&
                !isIntroStep(item) &&
                (isCallSpecificEditor || !isProtectedStep(item))
              }
              onDragStart={() => {
                if (
                  !item.id ||
                  isIntroStep(item) ||
                  (!isCallSpecificEditor && isProtectedStep(item)) ||
                  !canEditForm
                ) return;
                setDraggedStepId(item.id);
              }}
              onDragOver={(e) => {
                if (
                  !item.id ||
                  !draggedStepId ||
                  draggedStepId === item.id ||
                  !canEditForm ||
                  isIntroStep(item) ||
                  (!isCallSpecificEditor && isProtectedStep(item))
                )
                  return;
                e.preventDefault();
                setDragOverStepId(item.id);
              }}
              onDrop={(e) => {
                e.preventDefault();
                if (
                  !item.id ||
                  isIntroStep(item) ||
                  (!isCallSpecificEditor && isProtectedStep(item)) ||
                  !canEditForm
                ) return;
                void handleDropStep(item.id);
              }}
              onDragEnd={() => {
                setDraggedStepId(null);
                setDragOverStepId(null);
              }}
            >
              <div className="flex items-center gap-2">
                <GripVertical className="h-4 w-4 text-slate-400" />
                <div
                  className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold ${
                    selectedStep === item.step
                      ? "bg-emerald-500 text-white"
                      : "bg-slate-200 text-slate-700"
                  }`}
                >
                  {item.step}
                </div>
              </div>

              <div className="flex w-full flex-col gap-1">
                {editStep === item.step ? (
                  <input
                    type="text"
                    value={stepLabelDraft}
                    onChange={(e) => setStepLabelDraft(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    className="w-full rounded-md border border-slate-300 bg-white px-2 py-1 text-sm font-medium"
                    disabled={!canEditForm}
                  />
                ) : (
                  <p className="m-0 line-clamp-2 text-sm font-medium text-slate-800">
                    {item.nameStep}
                  </p>
                )}
              </div>

              <div className="flex items-center justify-end">
                {canEditForm &&
                editStep === item.step &&
                !isStepLockedForEdition(item) ? (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      void handleSaveStepLabel(item);
                    }}
                    type="button"
                    disabled={isBusy}
                  >
                    <CheckCircle className="text-emerald-500" />
                  </button>
                ) : canEditForm && !isStepLockedForEdition(item) ? (
                  <Popover>
                    <PopoverTrigger asChild>
                      <div
                        onClick={(e) => e.stopPropagation()}
                        className="cursor-pointer rounded-md p-1 hover:bg-slate-200"
                      >
                        <EllipsisVertical className="h-4 w-4 text-slate-700" />
                      </div>
                    </PopoverTrigger>
                    <PopoverContent className="w-40 p-2">
                      <button
                        className="mb-1 flex w-full flex-row gap-2 rounded-md px-2 py-1 text-left text-xs hover:bg-slate-100"
                        onClick={(e) => {
                          e.stopPropagation();
                          setStepLabelDraft(item.nameStep);
                          setEditStep(item.step);
                        }}
                        type="button"
                      >
                        <SquarePen className="h-3.5 w-3.5" />
                        Modifier
                      </button>
                      {!isStepLockedForEdition(item) && (
                        <button
                          className="mt-1 flex w-full flex-row gap-2 rounded-md px-2 py-1 text-left text-xs text-red-600 hover:bg-red-50"
                          onClick={(e) => {
                            e.stopPropagation();
                            void handleDeleteStep(item);
                          }}
                          type="button"
                        >
                          <Trash className="h-3.5 w-3.5" />
                          Supprimer
                        </button>
                      )}
                    </PopoverContent>
                  </Popover>
                ) : null}
              </div>
            </button>
          ))}

          {canEditForm && (
            <button
              className="mt-2 flex w-full items-center gap-3 rounded-xl border border-dashed border-emerald-300 bg-emerald-50 p-3 text-sm font-medium text-emerald-700 transition-colors hover:bg-emerald-100"
              onClick={() => void handleAddStep()}
              type="button"
              disabled={isBusy}
            >
              <CirclePlus size={20} />
              Ajouter une étape
            </button>
          )}
        </ScrollArea>

        <div className="min-w-0 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="mb-4 border-b border-slate-100 pb-3">
            {isBusy && (
              <p className="mt-1 text-xs font-medium text-slate-500">
                Synchronisation en cours...
              </p>
            )}
            {inlineError && (
              <p className="mt-1 text-xs font-medium text-red-600">
                {inlineError}
              </p>
            )}
            {!canEditForm && (
              <p className="mt-1 text-xs font-medium text-amber-700">
                Vous disposez uniquement d&apos;un accès en lecture sur ce
                formulaire.
              </p>
            )}
          </div>

          <CreateForm
            data={fields}
            sections={sections}
            stepId={selectedStep}
            stepDbId={selectedStepData?.id}
            stepDescription={selectedStepData?.description}
            canEditStepDescription={!isStepLockedForEdition(selectedStepData)}
            allowScoring={!isCallSpecificEditor || isCallFormScoringActive}
            hasMapFieldInForm={hasMapFieldInForm}
            hasFundingFieldInForm={isCallSpecificEditor ? hasFundingFieldInForm : false}
            hiddenFieldKeys={
              isCallSpecificEditor && isProtectedDescriptifStep
                ? HIDDEN_CALL_DESCRIPTIF_FIELDS
                : []
            }
            busy={isBusy}
            inlineError={inlineError}
            readOnly={!canEditForm}
            isProtectedDescriptifStep={isProtectedDescriptifStep}
            isFinancingPreviewStep={
              !isCallSpecificEditor && isLockedFinancingStep(selectedStepData)
            }
            isIntroStep={isIntroStep(selectedStepData)}
            protectedFieldKeys={Array.from(PROTECTED_DESCRIPTIF_FIELDS)}
            onSaveStepDescription={handleSaveSelectedStepDescription}
            onCreateField={handleCreateField}
            onUpdateField={handleUpdateField}
            onDeleteField={handleDeleteField}
            onReorderItems={async (parentId, itemIds) => {
              if (!canEditForm) {
                return false;
              }
              setInlineError(null);
              if (!itemIds.length) {
                return true;
              }
              const items = itemIds.map((id, index) => ({
                id,
                step: index + 1,
              }));
              const result = await postData({
                url: appendScope(`${API_URL}/field_forms/reorder`),
                data: {
                  parentId,
                  items,
                },
              });

              if (!result?.success) {
                const message = "Réorganisation impossible.";
                toast({ title: message });
                setInlineError(message);
                return false;
              }

              if (selectedStepData) {
                await loadSteps(selectedStepData.step, selectedStepData.id);
              }
              return true;
            }}
            onCreateSection={handleCreateSection}
            onUpdateSection={handleUpdateSection}
            onDeleteSection={handleDeleteSection}
            stepName={selectedStepData?.nameStep}
            projectTypeOptions={projectTypeOptions}
            projectTypeOptionsLoading={projectTypes.loading}
            stepVisibilityMode={selectedStepData?.visibilityMode ?? "all"}
            stepVisibilityProjectTypeIds={
              selectedStepData?.visibilityProjectTypeIds ?? []
            }
            onUpdateStepVisibility={(mode, projectTypeIds) =>
              selectedStepData
                ? handleUpdateStepVisibility(selectedStepData, mode, projectTypeIds)
                : Promise.resolve()
            }
            onUpdateFieldVisibility={handleUpdateFieldVisibility}
          />
        </div>
      </div>
      <Dialog open={scoringSettingsOpen} onOpenChange={setScoringSettingsOpen}>
        <DialogContent className="sm:max-w-[680px]">
          <DialogHeader>
            <DialogTitle>Paramétrer le système de notation</DialogTitle>
            <DialogDescription>
              Choisissez comment le score du candidat influencera la validation
              ou le rejet du projet dans cet appel à projets.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-sm font-medium text-slate-800">
                Mode de décision
              </p>
              <p className="mt-1 text-xs leading-5 text-slate-600">
                Le mode choisi sera conservé avec ce modèle d&apos;appel et sera
                dupliqué si vous réutilisez ce formulaire pour un autre appel.
              </p>
              <div className="mt-4 space-y-3">
                <Select
                  value={scoringSettingsDraft.decisionMode}
                  onValueChange={(value: CallFormScoringDecisionMode) =>
                    setScoringSettingsDraft((current) => ({
                      ...current,
                      enabled: true,
                      decisionMode: value,
                      acceptancePercentage:
                        value === "manual_total"
                          ? null
                          : current.acceptancePercentage,
                      rejectionPercentage: current.rejectionPercentage,
                    }))
                  }
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Sélectionner une stratégie de validation" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto_threshold">
                      Validation automatique basée uniquement sur les notes
                    </SelectItem>
                    {allowAdvancedCallScoringModes && (
                      <SelectItem value="manual_total">
                        Je décide moi-même après l&apos;évaluation du score global
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
                {!allowAdvancedCallScoringModes && (
                  <p className="text-xs leading-5 text-slate-600">
                    Pour un appel à projets géré avec Verdeo, seule la validation
                    automatique sur les notes automatiques est disponible. La
                    validation finale reste ensuite du ressort de l&apos;administration.
                  </p>
                )}
                {!scoringSettingsDraft.decisionMode && (
                  <p className="text-xs text-red-600">
                    Veuillez sélectionner une stratégie de validation.
                  </p>
                )}
              </div>
            </div>

            {scoringSettingsDraft.decisionMode && (
              <div className="rounded-2xl border border-[#dbe7d3] bg-[#f8fbf5] p-4">
                <p className="text-sm font-medium text-slate-800">
                  {scoringSettingsDraft.decisionMode === "auto_threshold"
                    ? "Validation ou rejet automatiques selon le score du formulaire"
                    : "Décision manuelle après calcul du score"}
                </p>
                <p className="mt-1 text-xs leading-5 text-slate-600">
                  {scoringSettingsDraft.decisionMode === "auto_threshold"
                    ? "Le projet est validé ou rejeté automatiquement selon les seuils configurés. Si le résultat se situe entre les deux seuils, la décision finale reste manuelle."
                    : "Le système calcule le score global. En dessous du seuil de rejet, le projet est rejeté automatiquement. Au-delà, la décision finale reste entre les mains du porteur de projet."}
                </p>

                {scoringSettingsNeedRejectionThreshold && (
                  <div
                    className={`mt-4 grid gap-4 ${
                      scoringSettingsNeedAcceptanceThreshold
                        ? "md:grid-cols-2"
                        : "md:grid-cols-1"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <label className="text-sm font-medium text-slate-700">
                        Seuil minimal de rejet
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          min={0}
                          max={100}
                          className="w-28 rounded-md border border-slate-300 px-3 py-2 text-sm"
                          value={
                            scoringSettingsDraft.rejectionPercentage === null ||
                            scoringSettingsDraft.rejectionPercentage === undefined
                              ? ""
                              : String(scoringSettingsDraft.rejectionPercentage)
                          }
                          onChange={(e) =>
                            setScoringSettingsDraft((current) => ({
                              ...current,
                              rejectionPercentage:
                                e.target.value === "" ? null : Number(e.target.value),
                            }))
                          }
                        />
                        <span className="text-sm text-slate-600">%</span>
                      </div>
                    </div>
                    {scoringSettingsNeedAcceptanceThreshold && (
                      <div className="flex items-center gap-3">
                        <label className="text-sm font-medium text-slate-700">
                          Seuil minimal d&apos;acceptation
                        </label>
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            min={0}
                            max={100}
                            className="w-28 rounded-md border border-slate-300 px-3 py-2 text-sm"
                            value={
                              scoringSettingsDraft.acceptancePercentage === null ||
                              scoringSettingsDraft.acceptancePercentage === undefined
                                ? ""
                                : String(scoringSettingsDraft.acceptancePercentage)
                            }
                            onChange={(e) =>
                              setScoringSettingsDraft((current) => ({
                                ...current,
                                acceptancePercentage:
                                  e.target.value === ""
                                    ? null
                                    : Number(e.target.value),
                              }))
                            }
                          />
                          <span className="text-sm text-slate-600">%</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {scoringSettingsNeedRejectionThreshold &&
                  (!Number.isFinite(parsedRejectionThreshold) ||
                    (scoringSettingsNeedAcceptanceThreshold &&
                      !Number.isFinite(parsedAcceptanceThreshold))) && (
                  <p className="mt-2 text-xs text-red-600">
                    {scoringSettingsNeedAcceptanceThreshold
                      ? "Veuillez renseigner les seuils de rejet et d’acceptation."
                      : "Veuillez renseigner le seuil de rejet."}
                  </p>
                )}
                {scoringSettingsNeedRejectionThreshold &&
                  (((scoringSettingsNeedAcceptanceThreshold &&
                    Number.isFinite(parsedAcceptanceThreshold) &&
                    (parsedAcceptanceThreshold < 0 ||
                      parsedAcceptanceThreshold > 100)) ||
                    (Number.isFinite(parsedRejectionThreshold) &&
                      (parsedRejectionThreshold < 0 ||
                        parsedRejectionThreshold > 100))) && (
                    <p className="mt-2 text-xs text-red-600">
                      Les seuils doivent être compris entre 0 et 100.
                    </p>
                  ))}
                {scoringSettingsNeedAcceptanceThreshold &&
                  Number.isFinite(parsedAcceptanceThreshold) &&
                  Number.isFinite(parsedRejectionThreshold) &&
                  parsedRejectionThreshold > parsedAcceptanceThreshold && (
                    <p className="mt-2 text-xs text-red-600">
                      Le seuil de rejet doit être inférieur ou égal au seuil d&apos;acceptation.
                    </p>
                  )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setScoringSettingsOpen(false)}>
              Annuler
            </Button>
            <Button
              type="button"
              onClick={() => void handleSaveCallFormScoringSettings()}
              disabled={!isScoringSettingsValid || isBusy || !canEditForm}
            >
              Enregistrer le paramétrage
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog
        open={blockedDeleteDialogOpen}
        onOpenChange={setBlockedDeleteDialogOpen}
      >
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>Suppression indisponible</DialogTitle>
            <DialogDescription>
              Ce formulaire est encore utilisé par un ou plusieurs appels à
              projets ou projets. Veuillez d'abord retirer ou réaffecter les
              éléments qui en dépendent.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setBlockedDeleteDialogOpen(false)}
            >
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>Supprimer ce formulaire ?</DialogTitle>
            <DialogDescription>
              Cette action supprimera définitivement le modèle et sa structure.
              Elle est irréversible.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={deletingForm}
            >
              Annuler
            </Button>
            <Button
              type="button"
              className="bg-red-600 hover:bg-red-700"
              onClick={() => void handleConfirmDeleteCurrentForm()}
              disabled={deletingForm}
            >
              {deletingForm ? "Suppression..." : "Supprimer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default ParametersForms;
